import React, { useState, useMemo, useEffect, useContext } from 'react';
import axios from 'axios';
import { themeColors } from '../theme';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';
import LiteratureSuggestForm from './LiteratureSuggestForm';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

const FEEDBACK_TYPES = [
  { value: 'bug',     label: 'Bug report' },
  { value: 'feature', label: 'Feature request' },
  { value: 'physics', label: 'Physics question' },
  { value: 'general', label: 'General feedback' },
];

const MAX_MSG = 500;

function validate(form) {
  if (!form.name.trim())    return 'Name is required';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()))
    return 'Please enter a valid email address';
  if (!form.type)           return 'Choose a feedback type';
  if (!form.message.trim()) return 'Message is required';
  if (form.message.length > MAX_MSG)
    return `Message too long (${form.message.length}/${MAX_MSG})`;
  return null;
}

export default function FeedbackPanel({ results, theme = 'dark' }) {
  const c = themeColors(theme);
  const inputStyle = {
    width: '100%', padding: '8px 10px',
    background: c.input, border: `1px solid ${c.border}`, color: c.inputText,
    fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
    borderRadius: 3, outline: 'none',
  };
  const labelStyle = {
    display: 'block', marginBottom: 4, fontSize: 12,
    color: c.textMuted, fontFamily: 'IBM Plex Mono, monospace',
    textTransform: 'uppercase', letterSpacing: 0.5,
  };
  const btnStyle = {
    padding: '10px 22px', fontSize: 13, fontFamily: 'IBM Plex Mono, monospace',
    background: c.btnPrimary, border: `1px solid ${c.btnPrimary}`,
    color: c.btnPrimaryText,
    borderRadius: 3, cursor: 'pointer', fontWeight: 500,
  };
  const btnDisabled = {
    ...btnStyle, background: c.btnInactive, borderColor: c.border,
    color: c.btnInactiveText, cursor: 'not-allowed',
  };
  const [form, setForm] = useState({
    name: '', email: '', type: 'bug', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);   // {kind:'ok'|'err', text}

  // ── Stage 2: publish to WorkspaceContext (feedback block) ────────
  // PRIVACY: never publish form.message or form.email. The chat only
  // sees whether a draft exists and its kind.
  const publish = useContext(WorkspaceContextSetter);
  useEffect(() => {
    publish('feedback', {
      draft_present: !!(form.message?.trim() || form.name?.trim()),
      draft_kind: form.type || null,
      submitting,
      last_status: status,
    });
  }, [publish, form.message, form.name, form.type, submitting, status]);

  const simContext = useMemo(() => {
    if (!results) return null;
    const m = results.metrics || {};
    const ctx = {
      metrics: {
        Voc: m.Voc, Jsc: m.Jsc, FF: m.FF, PCE: m.PCE,
      },
    };
    if (results._deviceContext) ctx.device = results._deviceContext;
    return ctx;
  }, [results]);

  const validationErr = validate(form);

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate(form);
    if (err) { setStatus({ kind: 'err', text: err }); return; }
    setSubmitting(true);
    setStatus(null);
    try {
      const { data } = await axios.post(`${API}/feedback`, {
        ...form,
        name:    form.name.trim(),
        email:   form.email.trim(),
        message: form.message.trim(),
        simulation_context: simContext,
      });
      setStatus({
        kind: 'ok',
        text: (data.persisted && data.emailed)
                ? 'Thanks — feedback saved and email sent.'
          : data.persisted
                ? 'Thanks — feedback saved. (Email delivery delayed.)'
          : data.emailed
                ? 'Thanks — feedback emailed. (Database delayed.)'
          : 'Thanks — feedback received.',
      });
      setForm({ name: '', email: '', type: 'bug', message: '' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const txt = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join('; ')
          : e.message || 'Network error';
      setStatus({ kind: 'err', text: txt });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ padding: 20, color: c.text, background: c.bg,
                   height: '100%', overflowY: 'auto', boxSizing: 'border-box',
                   fontFamily: 'IBM Plex Mono, monospace' }}>
      {/* Two responsive columns: feedback (left) + suggest-literature
          (right) side by side on wide screens, stacked when narrow —
          uses the horizontal space and keeps the page short. The root
          scrolls so nothing is ever cut off. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 32, alignItems: 'start', maxWidth: 1120,
      }}>
      <section>
      <h2 style={{ margin: 0, fontSize: 18, color: c.accentDark }}>
        Feedback
      </h2>
      <div style={{ fontSize: 13, color: c.textMuted, marginTop: 4, marginBottom: 20 }}>
        Report bugs, request features, or ask physics questions.  Your most
        recent simulation results are attached automatically as context.
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14 }}>
        <div>
          <label style={labelStyle} htmlFor="fb-name">Name</label>
          <input
            id="fb-name" style={inputStyle} maxLength={200} required
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor="fb-email">Email</label>
          <input
            id="fb-email" type="email" style={inputStyle} maxLength={200} required
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor="fb-type">Feedback type</label>
          <select
            id="fb-type" style={inputStyle}
            value={form.type}
            onChange={e => setForm({ ...form, type: e.target.value })}
          >
            {FEEDBACK_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle} htmlFor="fb-message">
            Message <span style={{ color: form.message.length > MAX_MSG ? c.red : c.textMuted }}>
              ({form.message.length}/{MAX_MSG})
            </span>
          </label>
          <textarea
            id="fb-message"
            style={{ ...inputStyle, height: 140, resize: 'vertical',
                      fontFamily: 'IBM Plex Mono, monospace' }}
            maxLength={MAX_MSG + 50}   // small grace; backend enforces 2000
            value={form.message}
            onChange={e => setForm({ ...form, message: e.target.value })}
          />
        </div>

        {simContext && simContext.metrics?.Jsc != null && (
          <div style={{ padding: 10, background: c.bgSecondary,
                         border: `1px solid ${c.border}`, borderRadius: 3,
                         fontSize: 12, color: c.textMuted }}>
            Attached simulation context: Voc={simContext.metrics.Voc} V,
            Jsc={simContext.metrics.Jsc} mA/cm²,
            FF={simContext.metrics.FF},
            PCE={simContext.metrics.PCE} %
            {simContext.device && ' + full device stack'}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            type="submit"
            style={submitting || validationErr ? btnDisabled : btnStyle}
            disabled={submitting || !!validationErr}
          >
            {submitting ? 'Sending…' : 'Send feedback'}
          </button>
          {status && (
            <span style={{
              fontSize: 13,
              color: status.kind === 'ok' ? c.green : c.red,
            }}>
              {status.text}
            </span>
          )}
        </div>
      </form>
      </section>

      {/* ── Suggest literature for the knowledge base (second column) ──── */}
      <section>
        <h2 style={{ margin: 0, fontSize: 18, color: c.accentDark }}>
          Suggest literature for the knowledge base
        </h2>
        <div style={{ fontSize: 13, color: c.textMuted,
                       marginTop: 4, marginBottom: 16 }}>
          Help grow JVISTA's curated knowledge base. Submissions go to a
          review queue — nothing is added automatically.
        </div>
        <LiteratureSuggestForm theme={theme} />
      </section>
      </div>
    </div>
  );
}
